import { useState } from "react";
import { Button, message } from "antd";
import { PlusOutlined, ExportOutlined } from "@ant-design/icons";
import { httpService } from "../../../../../services/httpService.service";

/**
 * "The facility / additional tool I need doesn't exist yet."
 *
 * Before this, the only way out was to abandon a half-filled site visit card,
 * go to the Planning > Facilities (or Additional Tools) tab, create the row and
 * start over. This gives the field the same escape hatch the Team section has
 * with "Create team card": create it right here, or open the full management
 * tab in a new browser tab so the card in progress survives.
 */
const CatalogQuickAdd = ({
  addLabel,
  managePath,
  manageLabel,
  listPath,
  createPath,
  buildPayload,
  renderModal,
  onCreated,
  successText,
  errorText,
}) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleCreate = (values) => {
    if (saving) return;
    setSaving(true);

    httpService.post(
      createPath,
      buildPayload(values),
      () => {
        // The create endpoints don't return the new row in a shape we can rely
        // on, so re-read the list and pick out the one we just named — that is
        // what lets us pre-select it for the user.
        httpService.get(
          listPath,
          (res) => {
            // /api/Facility answers with an envelope, /api/Datachecks/getAll
            // has been seen returning the bare array — accept either.
            const list = Array.isArray(res) ? res : res?.data || [];
            const wanted = (values?.name || "").trim().toLowerCase();
            const created = list.find(
              (item) => (item?.name || "").trim().toLowerCase() === wanted
            );
            onCreated(list, created);
            message.success(successText);
            setOpen(false);
            setSaving(false);
          },
          () => {
            setOpen(false);
            setSaving(false);
          }
        );
      },
      () => {
        message.error(errorText);
        setSaving(false);
      }
    );
  };

  return (
    <>
      <span className="catalogQuickAdd">
        <Button
          type="link"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setOpen(true)}
        >
          {addLabel}
        </Button>
        <span className="catalogQuickAddDot">·</span>
        <Button
          type="link"
          size="small"
          icon={<ExportOutlined />}
          // New tab on purpose: navigating away would throw away the card the
          // user is in the middle of filling in.
          onClick={() => window.open(managePath, "_blank", "noopener")}
        >
          {manageLabel}
        </Button>
      </span>
      {renderModal({
        visible: open,
        onCancel: () => setOpen(false),
        onOk: handleCreate,
      })}
    </>
  );
};

export default CatalogQuickAdd;
